<template>
  <div>
    <b-modal
      :id="modalId"
      size="lg"
      modal-class="job-form-modal-shell"
      body-class="job-form-modal-body"
      header-class="job-form-modal-header"
      footer-class="job-form-modal-footer"
      @hidden="resetForm"
    >
      <template #modal-title>
        <div class="job-form-modal-title">{{ modalTitle }}</div>
        <div v-if="modalSubtitle" class="job-form-modal-subtitle">
          {{ modalSubtitle }}
        </div>
      </template>

      <div class="job-form">
        <!-- Repeat summary for a job that already belongs to a series. One
             readable line by default; the picker only unfolds on request, so
             the common edit (a time, a name) isn't buried under it. -->
        <div v-if="isEdit && recurrenceGroupId" class="job-form-series-line">
          <div class="job-form-series-text">
            <span v-if="seriesLoading">Loading repeat info…</span>
            <span v-else-if="seriesSummary">{{ seriesSummaryText }}</span>
            <span v-else>Part of a repeating series</span>
          </div>
          <b-button
            v-if="seriesSummary"
            variant="outline-secondary"
            size="sm"
            :aria-expanded="showRepeatEditor ? 'true' : 'false'"
            @click="showRepeatEditor = !showRepeatEditor"
          >
            {{ showRepeatEditor ? 'Done' : 'Edit repeat' }}
          </b-button>
        </div>

        <template v-if="isEdit && recurrenceGroupId && showRepeatEditor">
          <repeat-days-picker v-if="seriesSummary" v-model="adjustConfig" />
          <p v-if="adjustRangeInvalid" class="job-form-time-error">
            Start date must be on or before the end date.
          </p>
          <p v-if="adjustDaysInvalid" class="job-form-time-error">
            A series must repeat on at least one day - pick one before saving.
          </p>
        </template>

        <b-form-group label="Job title" class="job-form-group">
          <b-form-input v-model="form.title" placeholder="Job title" />
        </b-form-group>

        <b-form-group label="Project (optional)" class="job-form-group">
          <b-form-select
            v-model="form.project_id"
            :options="projectSelectOptions"
          />
        </b-form-group>

        <!-- Repeating create: the pattern replaces the single date field
             entirely, so there's never a date and a pattern on screen at once. -->
        <template v-if="repeats">
          <repeat-days-picker v-model="repeatConfig" />
          <p v-if="repeatRangeInvalid" class="job-form-time-error">
            End date can't be before the start date.
          </p>
          <p v-else-if="repeatRangeTooLong" class="job-form-time-error">
            Date range can't be longer than a year.
          </p>
          <p v-else-if="noDaysSelected" class="job-form-time-error">
            Pick at least one day.
          </p>
          <p
            v-else-if="repeatPreviewText"
            class="job-form-preview"
            :class="{ 'job-form-preview--empty': occurrenceCount === 0 }"
          >
            {{ repeatPreviewText }}
          </p>
        </template>

        <b-form-group
          v-else
          label="Date"
          class="job-form-group"
          :state="dateFieldState"
          :invalid-feedback="dateError"
        >
          <b-form-datepicker
            v-model="form.date"
            class="job-datepicker"
            placeholder="dd/mm/yyyy"
            locale="en-AU"
            today-button
            reset-button
            no-flip
            required
            :date-format-options="{
              year: 'numeric',
              month: 'numeric',
              day: 'numeric',
            }"
          />
        </b-form-group>

        <b-row>
          <b-col md="6" class="mb-3 mb-md-0">
            <b-form-group label="Start time (optional)" class="job-form-group">
              <datetime-input v-model="form.start_time" type="time" />
            </b-form-group>
          </b-col>
          <b-col md="6">
            <b-form-group label="End time (optional)" class="job-form-group">
              <datetime-input v-model="form.end_time" type="time" />
            </b-form-group>
          </b-col>
        </b-row>

        <p v-if="isOvernightShift" class="job-form-overnight-hint">
          Overnight shift - ends {{ overnightEndText }}.
        </p>
        <p v-else-if="hasEqualTimes" class="job-form-time-error">
          Start and end time can't be the same.
        </p>

        <b-form-group label="Workers" class="job-form-group">
          <job-worker-assignment
            v-model="form.worker_ids"
            :project-id="form.project_id"
          />
        </b-form-group>

        <div class="job-form-divider" />

        <b-button
          class="job-form-disclosure"
          :aria-expanded="showMoreDetails ? 'true' : 'false'"
          aria-controls="job-form-more-details"
          @click="showMoreDetails = !showMoreDetails"
        >
          <span>More details</span>
          <span class="job-form-disclosure-hint">
            {{ moreDetailsHint }}
          </span>
        </b-button>

        <b-collapse id="job-form-more-details" v-model="showMoreDetails">
          <div class="job-form-more">
            <b-form-group label="Zone (optional)" class="job-form-group">
              <b-form-select
                v-model="form.zone_id"
                :options="zoneSelectOptions"
              />
            </b-form-group>

            <b-row>
              <b-col md="6" class="mb-3 mb-md-0">
                <b-form-group
                  label="Site name (optional)"
                  class="job-form-group"
                >
                  <b-form-input v-model="form.site_name" />
                </b-form-group>
              </b-col>
              <b-col md="6">
                <b-form-group label="Address (optional)" class="job-form-group">
                  <place-autocomplete
                    v-model="form.address"
                    :api-key="$env.placesApiKey"
                    :input-state="null"
                    :input-error="null"
                    :prepend-icon="false"
                    placeholder="Enter address"
                  ></place-autocomplete>
                </b-form-group>
              </b-col>
            </b-row>

            <b-form-group
              label="Instructions (optional)"
              class="job-form-group"
            >
              <b-form-textarea v-model="form.instructions" rows="3" />
            </b-form-group>

            <b-form-group class="job-form-group">
              <template #label>
                Supervisors (optional)
                <small class="job-form-label-help">
                  Who tradies contact. With none picked they see the project or
                  company supervisor.
                </small>
              </template>
              <job-supervisor-assignment
                v-model="form.supervisor_ids"
                :project-id="form.project_id"
              />
            </b-form-group>
          </div>
        </b-collapse>

        <!-- Scope is answered before Save, in the form, rather than by a
             second modal stacked on top of this one after clicking it. -->
        <div v-if="showScopeChoice" class="job-form-scope">
          <b-form-group class="job-form-group mb-0">
            <template #label>
              <span class="job-form-scope-label">Apply changes to</span>
            </template>
            <b-form-radio v-model="scopeChoice" name="job-scope" value="this">
              This date only
            </b-form-radio>
            <b-form-radio v-model="scopeChoice" name="job-scope" value="series">
              {{ seriesScopeLabel }}
            </b-form-radio>
          </b-form-group>
        </div>
      </div>

      <template #modal-footer>
        <job-actions-menu
          v-if="isEdit && actionsJob"
          :job="actionsJob"
          :series-loading="seriesLoading"
          inline
          @make-repeat="enterRepeatMode"
          @done="onActionDone"
        />
        <span v-else />

        <span class="job-form-modal-footer-right">
          <b-button variant="outline-secondary" size="sm" @click="hide">
            Close
          </b-button>
          <b-button
            variant="primary"
            size="sm"
            :disabled="saving || !isValid"
            @click="handleOk"
          >
            {{ saveLabel }}
          </b-button>
        </span>
      </template>
    </b-modal>

    <timesheet-conflict-modal ref="conflictModal" />
  </div>
</template>

<script>
import dayjs from 'dayjs'
import DatetimeInput from '@/components/timesheets/DatetimeInput.vue'
import PlaceAutocomplete from '@/components/autocomplete/PlaceAutocomplete'
import RepeatDaysPicker from '@/components/schedule/RepeatDaysPicker.vue'
import JobWorkerAssignment from '@/components/schedule/JobWorkerAssignment.vue'
import JobSupervisorAssignment from '@/components/schedule/JobSupervisorAssignment.vue'
import JobActionsMenu from '@/components/schedule/JobActionsMenu.vue'
import TimesheetConflictModal from '@/components/schedule/TimesheetConflictModal.vue'
import timesheetConflictRetry from '@/mixins/timesheetConflictRetry'

const DAY_CODES = ['sun', 'mon', 'tue', 'wed', 'thu', 'fri', 'sat']

const DEFAULT_FORM = () => ({
  id: null,
  title: '',
  project_id: null,
  zone_id: null,
  date: '',
  start_time: '',
  end_time: '',
  site_name: '',
  address: '',
  instructions: '',
  status: 'upcoming',
  worker_ids: [],
  supervisor_ids: [],
})

export default {
  name: 'JobFormModal',

  components: {
    DatetimeInput,
    PlaceAutocomplete,
    RepeatDaysPicker,
    JobWorkerAssignment,
    JobSupervisorAssignment,
    JobActionsMenu,
    TimesheetConflictModal,
  },

  mixins: [timesheetConflictRetry],

  props: {
    projectOptions: {
      type: Array,
      default: () => [],
    },
  },

  data() {
    return {
      modalId: 'job-form-modal',
      isEdit: false,
      saving: false,
      repeats: false,
      repeatConfig: { startDate: '', endDate: '', days: [] },
      // Set to a job id when that job's series was created but its own
      // delete failed or was declined (partial failure) — routes the next
      // Save click to retry only the delete, never re-create the series.
      pendingDeleteJobId: null,
      recurrenceGroupId: null,
      seriesSummary: null,
      seriesLoading: false,
      showRepeatEditor: false,
      showMoreDetails: false,
      adjustConfig: { startDate: '', endDate: '', days: [] },
      isCancelledJob: false,
      isCompletedJob: false,
      reopenMode: false,
      originalDate: '',
      scopeChoice: 'this',
      dateError: null,
      form: DEFAULT_FORM(),
      zones: [],
    }
  },

  computed: {
    modalTitle() {
      if (this.reopenMode) return 'Reopen Job'
      if (this.isEdit) return 'Edit job'
      return this.repeats ? 'New repeating job' : 'New job'
    },

    modalSubtitle() {
      if (this.isEdit) {
        const date = dayjs(this.form.date)
        return date.isValid() ? date.format('ddd D MMM') : ''
      }
      if (this.repeats) return 'Repeating'
      const date = dayjs(this.form.date)
      return date.isValid()
        ? `Single job · ${date.format('ddd D MMM')}`
        : 'Single job'
    },

    saveLabel() {
      if (this.saving) return 'Saving…'
      if (!this.isEdit && this.repeats && this.occurrenceCount > 0) {
        return `Create ${this.occurrenceCount} job${
          this.occurrenceCount === 1 ? '' : 's'
        }`
      }
      if (!this.isEdit) return 'Create job'
      return 'Save'
    },

    dateFieldState() {
      return this.dateError ? false : null
    },

    // The job shape the ⋯ menu needs. Built from the form rather than passed
    // in, so it reflects what's on screen (including a status the BA just
    // changed via the menu itself).
    actionsJob() {
      if (!this.isEdit || !this.form.id) return null
      return {
        id: this.form.id,
        title: this.form.title,
        date: this.form.date,
        status: this.form.status,
        recurrence_group_id: this.recurrenceGroupId,
        series_job_count: this.seriesSummary
          ? this.seriesSummary.job_count
          : null,
        // Lets the ⋯ menu hide "Cancel series" / "Reopen series" once
        // there's nothing left for them to do (e.g. the whole series is
        // completed) — see jobActionItems()'s when() for cancel-series /
        // reopen-series. null while seriesSummary hasn't loaded yet; paired
        // with the seriesLoading prop below, jobActionItems() renders those
        // items pending (disabled, "Checking…") rather than clickable until
        // the fetch resolves.
        cancellable_count: this.seriesSummary
          ? this.seriesSummary.cancellable_count
          : null,
        reopenable_count: this.seriesSummary
          ? this.seriesSummary.reopenable_count
          : null,
      }
    },

    // Converting an existing job into a series only makes sense when it
    // isn't already part of one (series membership has its own actions:
    // reopen/detach/cancel/delete series) and when it's still active
    // (a cancelled job has no "active" occurrence left to convert).
    canShowRepeats() {
      return !this.isEdit || (!this.recurrenceGroupId && !this.isCancelledJob)
    },

    showScopeChoice() {
      return this.isEdit && !!this.recurrenceGroupId && !this.repeats
    },

    seriesScopeLabel() {
      const count = this.seriesSummary ? this.seriesSummary.job_count : null
      return count ? `All upcoming dates · ${count} jobs` : 'All upcoming dates'
    },

    seriesSummaryText() {
      if (!this.seriesSummary) return ''
      const days = (this.seriesSummary.days || [])
        .map((d) => d.charAt(0).toUpperCase() + d.slice(1))
        .join(', ')
      const end = dayjs(this.seriesSummary.end_date)
      const until = end.isValid() ? ` until ${end.format('D MMM')}` : ''
      return `Repeats ${days}${until} · ${this.seriesSummary.job_count} jobs`
    },

    moreDetailsHint() {
      if (!this.showMoreDetails && this.filledDetailCount > 0) {
        return `${this.filledDetailCount} filled`
      }
      return this.showMoreDetails ? '' : 'Zone, site, instructions, supervisors'
    },

    filledDetailCount() {
      return [
        this.form.zone_id,
        this.form.site_name,
        this.form.address,
        this.form.instructions,
        this.form.supervisor_ids.length ? 'yes' : '',
      ].filter(Boolean).length
    },

    requireDateChange() {
      // Reopening requires a today-or-future date; the backend rejects
      // past-dated reopens with a 422.
      return (
        this.reopenMode &&
        !!this.form.date &&
        dayjs(this.form.date).isBefore(dayjs(), 'day')
      )
    },

    projectSelectOptions() {
      return [{ value: null, text: '- No project -' }, ...this.projectOptions]
    },

    zoneSelectOptions() {
      return [
        { value: null, text: '- No zone -' },
        ...this.zones.map((z) => ({ value: z.id, text: z.name })),
      ]
    },

    // An end time earlier than the start is a valid overnight shift; only
    // identical times are rejected.
    hasEqualTimes() {
      return (
        !!this.form.start_time &&
        !!this.form.end_time &&
        this.form.start_time === this.form.end_time
      )
    },

    isOvernightShift() {
      return (
        !!this.form.start_time &&
        !!this.form.end_time &&
        this.form.end_time < this.form.start_time
      )
    },

    // The date the shift finishes on. Concrete for a single-date job; for a
    // recurring series the first occurrence stands in, so keep it vague.
    overnightEndText() {
      const startDate = this.repeats
        ? this.repeatConfig.startDate
        : this.form.date
      if (!startDate) return 'the next morning'

      const end = dayjs(startDate).add(1, 'day')
      if (!end.isValid()) return 'the next morning'

      return `${this.form.end_time} on ${end.format('ddd D MMM')}`
    },

    // Every date the current pattern would actually produce. The single
    // source for the count, the preview line, the Save label and the
    // zero-match guard — the API loops the same range server-side but
    // returns 200 with an empty array rather than a validation error, so the
    // BA needs to see the number before they commit, not after.
    occurrenceDates() {
      const { startDate, endDate, days } = this.repeatConfig
      if (!startDate || !endDate || !days.length) return []
      if (endDate < startDate || this.repeatRangeTooLong) return []

      const dates = []
      let cursor = dayjs(startDate)
      const end = dayjs(endDate)
      while (!cursor.isAfter(end, 'day')) {
        if (days.includes(DAY_CODES[cursor.day()])) {
          dates.push(cursor.format('YYYY-MM-DD'))
        }
        cursor = cursor.add(1, 'day')
      }
      return dates
    },

    occurrenceCount() {
      return this.occurrenceDates.length
    },

    repeatPreviewText() {
      const { startDate, endDate, days } = this.repeatConfig
      if (!startDate || !endDate || !days.length) return ''

      if (this.occurrenceCount === 0) {
        return 'Creates 0 jobs - no dates in this range fall on the days you picked.'
      }

      const labels = days
        .map((d) => d.charAt(0).toUpperCase() + d.slice(1))
        .join(', ')
      const first = dayjs(this.occurrenceDates[0]).format('D MMM')
      const last = dayjs(this.occurrenceDates[this.occurrenceCount - 1]).format(
        'D MMM',
      )

      return `Creates ${this.occurrenceCount} job${
        this.occurrenceCount === 1 ? '' : 's'
      }, every ${labels} from ${first} to ${last}.`
    },

    // True once both dates are picked and in order — false while incomplete,
    // so this alone can't be used to show the "end before start" error (an
    // empty end date isn't a range error, just an incomplete one).
    repeatRangeValid() {
      return (
        !!this.repeatConfig.startDate &&
        !!this.repeatConfig.endDate &&
        this.repeatConfig.endDate >= this.repeatConfig.startDate &&
        !this.repeatRangeTooLong &&
        this.repeatConfig.days.length > 0 &&
        !this.hasNoMatchingOccurrences
      )
    },

    // Only worth showing once both dates are actually picked — an empty end
    // date shouldn't flash this error before the user has had a chance to
    // pick one.
    repeatRangeInvalid() {
      return (
        !!this.repeatConfig.startDate &&
        !!this.repeatConfig.endDate &&
        this.repeatConfig.endDate < this.repeatConfig.startDate
      )
    },

    // Caps the span so a fat-fingered multi-year range can't fan out into an
    // unbounded number of created jobs — the API has no cap of its own, it
    // just loops every day in range. ~1 year covers any legitimate repeat
    // schedule.
    repeatRangeTooLong() {
      return (
        !!this.repeatConfig.startDate &&
        !!this.repeatConfig.endDate &&
        this.repeatConfig.endDate >= this.repeatConfig.startDate &&
        dayjs(this.repeatConfig.endDate).diff(
          dayjs(this.repeatConfig.startDate),
          'day',
        ) > 366
      )
    },

    // Only worth showing once a valid date range exists — otherwise this
    // would flash before the user has picked dates at all, alongside (never
    // instead of) the range error above.
    noDaysSelected() {
      return (
        !!this.repeatConfig.startDate &&
        !!this.repeatConfig.endDate &&
        this.repeatConfig.endDate >= this.repeatConfig.startDate &&
        !this.repeatRangeTooLong &&
        this.repeatConfig.days.length === 0
      )
    },

    // A valid-looking range + day selection can still match zero actual
    // dates (e.g. Start = End = a Monday, but only "Tue" is checked). Shown
    // as "Creates 0 jobs" in the preview rather than as a late error.
    hasNoMatchingOccurrences() {
      if (
        !this.repeatConfig.startDate ||
        !this.repeatConfig.endDate ||
        this.repeatConfig.endDate < this.repeatConfig.startDate ||
        this.repeatRangeTooLong ||
        this.repeatConfig.days.length === 0
      ) {
        return false
      }
      return this.occurrenceCount === 0
    },

    isValid() {
      if (!this.form.title) return false
      if (this.hasEqualTimes) return false
      if (this.isEdit && this.repeats) {
        return this.repeatRangeValid
      }
      if (this.isEdit) {
        if (!this.form.date) return false
        if (this.requireDateChange) return false
        if (
          this.recurrenceGroupId &&
          (this.adjustRangeInvalid || this.adjustDaysInvalid)
        ) {
          return false
        }
        return true
      }
      if (this.repeats) {
        return this.repeatRangeValid
      }
      return !!this.form.date
    },

    // Both extending and shrinking either edge are valid moves now — the
    // only hard rule (mirrors the server's finalStart <= finalEnd check) is
    // that the resulting range still makes sense.
    adjustRangeInvalid() {
      if (!this.adjustConfig.startDate || !this.adjustConfig.endDate) {
        return false
      }
      return this.adjustConfig.startDate > this.adjustConfig.endDate
    },

    // A series must always repeat on at least one day — removing every day
    // via the picker would leave the pattern empty. Same class of guard as
    // the server's min:1 validation on repeat_days, surfaced immediately
    // client-side instead of round-tripping to a 422.
    adjustDaysInvalid() {
      if (!this.seriesSummary) return false
      return this.adjustConfig.days.length === 0
    },

    // Which of the three fields actually differ from the series' current
    // pattern — the signal for whether adjustJobSeries should be called at
    // all, kept apart from the *Invalid computeds above (those only flag
    // genuine errors; equal-to-current is neither an error nor a request).
    adjustRequested() {
      if (!this.seriesSummary) return false
      const startChanged =
        !!this.adjustConfig.startDate &&
        this.adjustConfig.startDate !== this.seriesSummary.start_date
      const endChanged =
        !!this.adjustConfig.endDate &&
        this.adjustConfig.endDate !== this.seriesSummary.end_date
      const daysChanged =
        this.adjustConfig.days.length !== this.seriesSummary.days.length ||
        this.adjustConfig.days.some((d) => !this.seriesSummary.days.includes(d))
      return startChanged || endChanged || daysChanged
    },
  },

  watch: {
    'form.date'() {
      this.dateError = null
    },

    repeats(on) {
      // Pre-fill the series start with the job's existing date so
      // converting to a series doesn't accidentally skip the date already
      // on the calendar.
      if (on && this.isEdit && !this.repeatConfig.startDate) {
        this.repeatConfig = { ...this.repeatConfig, startDate: this.form.date }
      }
    },

    seriesSummary(val) {
      // openEdit() doesn't await _loadSeriesSummary(), so this can't be
      // pre-filled synchronously at open time — fill it once the async
      // summary resolves, but never clobber a config the user already
      // touched.
      if (val && !this.adjustConfig.startDate) {
        this.adjustConfig = {
          startDate: val.start_date,
          endDate: val.end_date,
          days: [...val.days],
        }
      }
    },
  },

  created() {
    this.loadZones()
  },

  methods: {
    async loadZones() {
      try {
        this.zones = await this.$store.dispatch('timesheet/getZones')
      } catch (e) {
        this.zones = []
      }
    },

    // mode comes from the chooser: 'single' or 'series'. The old "Repeats"
    // tickbox lived halfway down this form; the shape is now decided before
    // the form opens.
    openCreate(prefill = {}, mode = 'single') {
      this.isEdit = false
      this.repeats = mode === 'series'
      this.showMoreDetails = false
      this.form = { ...DEFAULT_FORM(), ...prefill }
      this.repeatConfig = this.repeats
        ? {
            startDate: prefill.date || '',
            endDate: '',
            days: prefill.date ? [DAY_CODES[dayjs(prefill.date).day()]] : [],
          }
        : { startDate: '', endDate: '', days: [] }
      this.$bvModal.show(this.modalId)
    },

    openEdit(job) {
      this.isEdit = true
      this.isCancelledJob = job.status === 'cancelled'
      this.isCompletedJob = job.status === 'completed'
      this.reopenMode = false
      this.repeats = false
      this.showRepeatEditor = false
      this.repeatConfig = { startDate: '', endDate: '', days: [] }
      // pendingDeleteJobId is deliberately NOT reset here: it must survive
      // closing and reopening the SAME job, or the retry-only-delete guard
      // in handleOk would be bypassed and re-checking Repeats would create
      // another duplicate series. It's gated by job id, so it's a no-op for
      // any other job.
      this.originalDate = job.date
      this.form = {
        id: job.id,
        title: job.title,
        project_id: job.project_id,
        zone_id: job.zone_id,
        date: job.date,
        start_time: job.start_time || '',
        end_time: job.end_time || '',
        site_name: job.site_name || '',
        address: job.address || '',
        instructions: job.instructions || '',
        status: job.status,
        worker_ids: (job.workers || []).map((w) => w.id),
        supervisor_ids: (job.supervisors || []).map((s) => s.id),
      }
      // Details the BA already filled in shouldn't hide behind a disclosure
      // they have to discover — open it when it has something in it.
      this.showMoreDetails = this.filledDetailCount > 0
      this.recurrenceGroupId = job.recurrence_group_id || null
      this.scopeChoice = 'this'
      this.seriesSummary = null
      if (this.recurrenceGroupId) {
        this._loadSeriesSummary()
      }
      this.$bvModal.show(this.modalId)
    },

    openReopen(job) {
      this.openEdit(job)
      this.isCancelledJob = true
      this.enterReopenMode()
    },

    enterReopenMode() {
      this.reopenMode = true
      this.form.status = 'upcoming'
    },

    // ⋯ → "Make this repeat…" on a single job. Same conversion the Repeats
    // tickbox used to trigger; only the entry point moved.
    enterRepeatMode() {
      if (!this.canShowRepeats) return
      this.repeats = true
      this.repeatConfig = {
        startDate: this.form.date,
        endDate: '',
        days: this.form.date ? [DAY_CODES[dayjs(this.form.date).day()]] : [],
      }
    },

    hide() {
      this.$bvModal.hide(this.modalId)
    },

    setDateError(msg) {
      this.dateError = msg
    },

    // An action from the footer menu already ran and toasted; close on a real
    // change so the BA lands back on the refreshed grid.
    onActionDone(result) {
      if (result && result.dateError) {
        this.enterReopenMode()
        this.setDateError(result.dateError)
        return
      }
      if (result && result.changed) {
        this.$bvModal.hide(this.modalId)
        this.$emit('saved')
      }
    },

    async handleOk() {
      if (!this.isValid || this.saving) return

      if (this.isEdit && this.repeats) {
        // A prior attempt already created the series and only the delete
        // step failed (or was declined) — retry just the delete. Re-running
        // the full conversion here would call createJob again and mint a
        // second, duplicate series on top of the one that already exists.
        if (this.pendingDeleteJobId === this.form.id) {
          this.saving = true
          try {
            await this._retryDeleteOriginal()
          } finally {
            this.saving = false
          }
          return
        }

        if (!(await this._confirmConvertToSeries())) return // user declined
        this.saving = true
        try {
          await this._convertToSeries()
          this.$bvModal.hide(this.modalId)
        } catch (e) {
          // _convertToSeries already shows a specific toast for a partial
          // failure (series created, delete of the original failed or was
          // declined) and keeps the modal open — pendingDeleteJobId now
          // routes the next Save click to _retryDeleteOriginal instead of
          // back through here. It also self-toasts the zero-occurrences
          // case. Only a genuine failure creating the series itself falls
          // through to the generic toast here.
          if (!e.partialFailure && !e.alreadyToasted) {
            this.$bvToast.toast('Failed to update job.', {
              variant: 'danger',
              title: 'Error',
            })
          }
        } finally {
          this.saving = false
        }
        return
      }

      this.saving = true
      try {
        if (this.isEdit) {
          await this._updateJob(
            this.showScopeChoice ? this.scopeChoice : 'this',
          )
        } else {
          await this._createJob()
        }
        this.$bvModal.hide(this.modalId)
      } catch (e) {
        const dateErrors =
          e.response &&
          e.response.status === 422 &&
          e.response.data &&
          e.response.data.errors &&
          e.response.data.errors.date

        if (this.isEdit && dateErrors) {
          this.dateError = dateErrors[0]
        } else if (!e.alreadyToasted) {
          // alreadyToasted means _adjustSeries already showed its own
          // specific error toast — showing this generic one too would
          // double-toast the same failure.
          this.$bvToast.toast(
            this.isEdit ? 'Failed to update job.' : 'Failed to create job.',
            { variant: 'danger', title: 'Error' },
          )
        }
      } finally {
        this.saving = false
      }
    },

    // Shared by _createJob and _convertToSeries — both post to the same
    // createJob endpoint with the same job fields, only the date/repeat
    // fields differ.
    _jobPayloadBase() {
      return {
        title: this.form.title,
        project_id: this.form.project_id,
        zone_id: this.form.zone_id,
        start_time: this.form.start_time || null,
        end_time: this.form.end_time || null,
        site_name: this.form.site_name || null,
        address: this.form.address || null,
        instructions: this.form.instructions || null,
        worker_ids: this.form.worker_ids,
        supervisor_ids: this.form.supervisor_ids,
      }
    },

    // The week the BA should be looking at after a save: the earliest date
    // actually created. Without it a job created outside the visible week
    // saves successfully and the screen appears not to change.
    _earliestDate(jobs) {
      if (!jobs || !jobs.length) return undefined
      return jobs.reduce(
        (min, j) => (j.date < min ? j.date : min),
        jobs[0].date,
      )
    },

    async _createJob() {
      const payload = this._jobPayloadBase()

      if (this.repeats) {
        payload.repeat_start_date = this.repeatConfig.startDate
        payload.repeat_end_date = this.repeatConfig.endDate
        payload.repeat_days = this.repeatConfig.days
      } else {
        payload.date = this.form.date
      }

      const jobs = await this.$store.dispatch('schedule/createJob', payload)
      this.$bvToast.toast(
        jobs.length > 1 ? `${jobs.length} jobs created.` : 'Job created.',
        { variant: 'success', title: 'Created' },
      )
      this.$emit('saved', { jumpToDate: this._earliestDate(jobs) })
    },

    // One request for fields AND roster AND scope — the API applies them in a
    // single transaction, so a series edit can no longer half-apply the way
    // the old update/workers/supervisors call chain could.
    async _updateJob(applyScope = 'this') {
      this.dateError = null
      const job = await this.$store.dispatch('schedule/updateJob', {
        id: this.form.id,
        title: this.form.title,
        project_id: this.form.project_id,
        zone_id: this.form.zone_id,
        date: this.form.date,
        start_time: this.form.start_time || null,
        end_time: this.form.end_time || null,
        site_name: this.form.site_name || null,
        address: this.form.address || null,
        instructions: this.form.instructions || null,
        status: this.form.status,
        worker_ids: this.form.worker_ids,
        supervisor_ids: this.form.supervisor_ids,
        apply_scope: applyScope,
      })

      const applied = (job && job.applied_occurrences) || 1
      this.$bvToast.toast(
        this.reopenMode
          ? 'Job reopened.'
          : applied > 1
          ? `Updated ${applied} jobs in the series.`
          : 'Job updated.',
        {
          variant: 'success',
          title: this.reopenMode ? 'Reopened' : 'Updated',
        },
      )

      // Adjusting the series pattern stays its own request, run last, so a
      // failed adjust never leaves the field/roster edits above half-applied
      // — those already succeeded and are already toasted by the time this
      // runs.
      if (this.recurrenceGroupId && this.adjustRequested) {
        await this._adjustSeries()
        return
      }

      this.$emit('saved')
    },

    // Runs as the 2nd step of _updateJob when Start date, End date, and/or
    // Days-of-week were changed on the RepeatDaysPicker for an
    // already-recurring job.
    async _adjustSeries() {
      const payload = { recurrenceGroupId: this.recurrenceGroupId }
      // Either direction — extend or shrink — is sent whenever the field
      // actually differs from the series' current value.
      if (this.adjustConfig.startDate !== this.seriesSummary.start_date) {
        payload.repeatStartDate = this.adjustConfig.startDate
      }
      if (this.adjustConfig.endDate !== this.seriesSummary.end_date) {
        payload.repeatEndDate = this.adjustConfig.endDate
      }
      // repeat_days is sent as the full new set either way; the server
      // diffs it against job_series.repeat_days to work out which.
      const daysChanged =
        this.adjustConfig.days.length !== this.seriesSummary.days.length ||
        this.adjustConfig.days.some((d) => !this.seriesSummary.days.includes(d))
      if (daysChanged) {
        payload.repeatDays = this.adjustConfig.days
      }

      try {
        const { jobs, noOpFields, cancelledJobIds } =
          await this.withTimesheetConflictRetry(
            (forceArgs) =>
              this.$store.dispatch('schedule/adjustJobSeries', {
                ...payload,
                ...forceArgs,
              }),
            'Apply anyway',
          )
        this.$bvToast.toast(this._adjustSuccessMessage(jobs, cancelledJobIds), {
          variant: 'success',
          title: 'Series adjusted',
        })
        if (noOpFields.length) {
          this.$bvToast.toast(this._noOpFieldsMessage(noOpFields), {
            variant: 'warning',
            title: 'Note',
          })
        }
        await this._loadSeriesSummary()
        // jobs isn't guaranteed to be date-ordered (up to 3 generation
        // steps get merged server-side) — reduce for the earliest date
        // rather than trusting array order, so the view jumps to the right
        // week. A day-removal-only save has no new jobs to jump to — the
        // cancelled jobs are wherever the BA already is, not a place they
        // need discovering, so no jumpToDate for that case.
        this.$emit('saved', { jumpToDate: this._earliestDate(jobs) })
      } catch (e) {
        if (e && e.timesheetConflictCancelled) {
          // The job-field/roster changes in _updateJob already succeeded —
          // emit 'saved' (no jumpToDate) so the schedule reflects them, same
          // as every other adjust-failure path below, even though the user
          // declined the conflict dialog and the series itself is unchanged.
          this.$emit('saved')
          const cancelled = new Error('Timesheet conflict — cancelled')
          cancelled.alreadyToasted = true
          throw cancelled
        }

        const responseData = (e && e.response && e.response.data) || {}

        const fieldErrors =
          (responseData.errors &&
            (responseData.errors.repeat_start_date ||
              responseData.errors.repeat_end_date ||
              responseData.errors.repeat_days)) ||
          []
        const message =
          fieldErrors[0] || responseData.message || 'Failed to adjust series.'
        this.$bvToast.toast(message, { variant: 'danger', title: 'Error' })
        // The job-field/roster changes in _updateJob already succeeded and
        // are already toasted above — emit 'saved' (no jumpToDate) so the
        // schedule view reflects them instead of leaving the grid stale.
        this.$emit('saved')
        const adjustError = new Error('Failed to adjust series')
        adjustError.alreadyToasted = true
        throw adjustError
      }
    },

    // Combines the "N new job(s) added" and "N job(s) removed" halves of a
    // successful adjust into one message — either can be zero (a pure
    // removal adds nothing; a pure addition removes nothing).
    _adjustSuccessMessage(jobs, cancelledJobIds) {
      const parts = []
      if (jobs.length) {
        parts.push(`${jobs.length} new job(s) added`)
      }
      if (cancelledJobIds.length) {
        parts.push(`${cancelledJobIds.length} job(s) removed`)
      }
      return parts.length
        ? `${parts.join(', ')} in the series.`
        : 'Series updated.'
    },

    // "repeat_start_date" -> "the start-date change", etc — used to build a
    // human-readable note for fields that produced zero new occurrences
    // even though the overall adjust succeeded (see adjustSeries()'s
    // no_op_fields in the API response).
    _noOpFieldsMessage(noOpFields) {
      const labels = {
        repeat_start_date: 'the start-date change',
        repeat_end_date: 'the end-date change',
        repeat_days: 'the new day(s)',
      }
      const named = noOpFields.map((f) => labels[f] || f)
      const joined =
        named.length > 1
          ? `${named.slice(0, -1).join(', ')} and ${named[named.length - 1]}`
          : named[0]
      return `${joined} didn't add any occurrences - no matching date in range.`
    },

    _confirmConvertToSeries() {
      return window.confirm(
        'Convert this job to a repeating series? A new series will be ' +
          'created for the dates and days you picked, and this single job ' +
          'will be permanently deleted. Any logged timesheet hours for it ' +
          'are unaffected — they are tracked by worker and date, not by ' +
          'this job record.',
      )
    },

    // Frontend-only workaround: the API has no endpoint to convert an
    // existing job into a series in place, so this creates a new series via
    // the same path New Job uses, then deletes the original job. Order
    // matters — the delete only runs after the series create succeeds, so a
    // create failure leaves the original job untouched instead of
    // duplicating it. See docs/designs/edit-job-repeats.md.
    async _convertToSeries() {
      const jobs = await this.$store.dispatch('schedule/createJob', {
        ...this._jobPayloadBase(),
        repeat_start_date: this.repeatConfig.startDate,
        repeat_end_date: this.repeatConfig.endDate,
        repeat_days: this.repeatConfig.days,
      })

      // Defense in depth: isValid/hasNoMatchingOccurrences should already
      // block this client-side, but if the date range and selected days
      // somehow reach the API without matching any actual date, it returns
      // an empty jobs array with a 200 — no validation error. Treat that as
      // a failure and never touch the original job; the alternative is
      // deleting it for "0 job(s) created."
      if (jobs.length === 0) {
        this.$bvToast.toast(
          'No jobs were created for that date range and days - the ' +
            'original job was left untouched.',
          { variant: 'danger', title: 'Error' },
        )
        const zeroOccurrencesError = new Error(
          'createJob returned zero occurrences',
        )
        zeroOccurrencesError.alreadyToasted = true
        throw zeroOccurrencesError
      }

      try {
        await this.withTimesheetConflictRetry(
          (forceArgs) =>
            this.$store.dispatch('schedule/deleteJob', {
              id: this.form.id,
              ...forceArgs,
            }),
          'Delete anyway',
        )
      } catch (e) {
        this.pendingDeleteJobId = this.form.id
        if (e && e.timesheetConflictCancelled) {
          this.$bvToast.toast(
            `${jobs.length} job(s) created, but the original job wasn't ` +
              'deleted. Delete it manually to avoid a duplicate on the schedule.',
            { variant: 'warning', title: 'Series created', noAutoHide: true },
          )
          this.$emit('saved', { jumpToDate: this._earliestDate(jobs) })
          const declinedError = new Error(
            'User declined deleting original job after series creation',
          )
          declinedError.partialFailure = true
          throw declinedError
        }
        this.$bvToast.toast(
          `${jobs.length} job(s) created, but the original job couldn't be ` +
            'deleted. Delete it manually to avoid a duplicate on the schedule.',
          { variant: 'danger', title: 'Partial failure', noAutoHide: true },
        )
        this.$emit('saved', { jumpToDate: this._earliestDate(jobs) })
        const partialFailureError = new Error(
          'Failed to delete original job after series creation',
        )
        partialFailureError.partialFailure = true
        throw partialFailureError
      }

      this.$bvToast.toast(
        `${jobs.length} job(s) created. The original single job was deleted.`,
        { variant: 'success', title: 'Converted to series' },
      )
      this.$emit('saved', { jumpToDate: this._earliestDate(jobs) })
    },

    // Only reachable when a prior conversion attempt already created the
    // series and left this job's delete pending — retries just that call,
    // never re-creates the series.
    async _retryDeleteOriginal() {
      try {
        await this.withTimesheetConflictRetry(
          (forceArgs) =>
            this.$store.dispatch('schedule/deleteJob', {
              id: this.form.id,
              ...forceArgs,
            }),
          'Delete anyway',
        )
        this.pendingDeleteJobId = null
        this.$bvToast.toast(
          'Original job deleted. Series conversion complete.',
          {
            variant: 'success',
            title: 'Deleted',
          },
        )
        this.$bvModal.hide(this.modalId)
        this.$emit('saved')
      } catch (e) {
        if (e && e.timesheetConflictCancelled) return
        this.$bvToast.toast(
          "Still couldn't delete the original job. Delete it manually to " +
            'avoid a duplicate on the schedule.',
          { variant: 'danger', title: 'Partial failure', noAutoHide: true },
        )
      }
    },

    async _loadSeriesSummary() {
      this.seriesLoading = true
      try {
        this.seriesSummary = await this.$store.dispatch(
          'schedule/getJobSeries',
          this.recurrenceGroupId,
        )
      } catch (e) {
        this.seriesSummary = null
      } finally {
        this.seriesLoading = false
      }
    },

    resetForm() {
      this.form = DEFAULT_FORM()
      this.repeats = false
      this.repeatConfig = { startDate: '', endDate: '', days: [] }
      // pendingDeleteJobId is NOT reset here — see the comment in openEdit().
      this.recurrenceGroupId = null
      this.seriesSummary = null
      this.showRepeatEditor = false
      this.showMoreDetails = false
      this.adjustConfig = { startDate: '', endDate: '', days: [] }
      this.isCancelledJob = false
      this.isCompletedJob = false
      this.reopenMode = false
      this.originalDate = ''
      this.scopeChoice = 'this'
      this.dateError = null
    },
  },
}
</script>

<style>
.job-form-modal-shell .modal-content {
  overflow: hidden;
  border: 0;
  border-radius: 14px;
  box-shadow: 0 24px 70px rgba(15, 23, 42, 0.22);
}

.job-form-modal-header {
  padding: 1.1rem 1.25rem;
  border-bottom: 1px solid #e5e7eb;
  background: #f9fafb;
}

.job-form-modal-title {
  color: #111827;
  font-size: 1.1rem;
  font-weight: 700;
}

.job-form-modal-subtitle {
  margin-top: 2px;
  color: #6b7280;
  font-size: 0.8125rem;
  font-weight: 400;
}

.job-form-modal-body {
  padding: 0;
  background: #ffffff;
}

.job-form-modal-footer {
  justify-content: space-between;
  padding: 1rem 1.25rem;
  border-top: 1px solid #e5e7eb;
  background: #ffffff;
}

.job-form-modal-footer-right .btn + .btn {
  margin-left: 0.5rem;
}

/* One surface, not a stack of cards: groups are separated by spacing and a
   single hairline rule. */
.job-form {
  padding: 1.25rem;
  background: #ffffff;
}

.job-form-divider {
  height: 1px;
  margin: 1.25rem 0;
  background: #e5e7eb;
}

.job-form-group {
  margin-bottom: 0.9rem;
}

.job-form-group:last-child {
  margin-bottom: 0;
}

.job-form-group legend,
.job-form-group label {
  margin-bottom: 0.35rem;
  color: #374151;
  font-size: 0.75rem;
  font-weight: 700;
}

.job-form-label-help {
  display: block;
  color: #6b7280;
  font-size: 0.75rem;
  font-weight: 400;
}

.job-form .form-control,
.job-form .custom-select {
  min-height: 38px;
  border-color: #d1d5db;
  border-radius: 8px;
  color: #111827;
}

.job-form-overnight-hint {
  margin: 0.6rem 0 0;
  color: #6b7280;
  font-size: 0.75rem;
}

.job-form-time-error {
  margin: 0.6rem 0 0;
  color: #b91c1c;
  font-size: 0.75rem;
}

/* The count is the most useful thing on the repeat form — a BA creating two
   months of shifts is changing other people's rosters. */
.job-form-preview {
  margin: 0.6rem 0 0;
  padding: 0.6rem 0.75rem;
  border: 1px solid #e5e7eb;
  border-left: 3px solid #006cff;
  border-radius: 8px;
  color: #374151;
  font-size: 0.8125rem;
  font-weight: 600;
}

.job-form-preview--empty {
  border-left-color: #dc3545;
  color: #b91c1c;
}

.job-form-series-line {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 0.75rem;
  margin-bottom: 1rem;
  padding: 0.65rem 0.85rem;
  border: 1px solid #e5e7eb;
  border-radius: 8px;
  background: #f9fafb;
}

.job-form-series-text {
  color: #111827;
  font-size: 0.875rem;
  font-weight: 600;
}

.job-form-disclosure {
  display: flex;
  align-items: center;
  justify-content: space-between;
  width: 100%;
  min-height: 44px;
  padding: 0.6rem 0.75rem;
  border: 1px solid #e5e7eb;
  border-radius: 8px;
  background: #ffffff;
  color: #374151;
  font-size: 0.8125rem;
  font-weight: 700;
}

.job-form-disclosure:hover,
.job-form-disclosure:focus {
  border-color: #2c66d5;
  background: #f9fafb;
  box-shadow: none;
  color: #111827;
}

.job-form-disclosure-hint {
  color: #6b7280;
  font-size: 0.75rem;
  font-weight: 600;
}

.job-form-more {
  padding-top: 0.9rem;
}

.job-form-scope {
  margin-top: 1.25rem;
  padding: 0.75rem 0.85rem;
  border: 1px solid #e5e7eb;
  border-radius: 8px;
  background: #f9fafb;
}

.job-form-scope-label {
  color: #374151;
  font-size: 0.75rem;
  font-weight: 700;
}

.job-form /deep/ .datetime-field .form-control:focus {
  border: 1px solid #9ca3af;
  box-shadow: 0 0 0 3px rgba(156, 163, 175, 0.25);
}

.job-form /deep/ .job-datepicker.form-control {
  display: flex;
  align-items: center;
  min-height: 38px;
  border-color: #d1d5db;
  border-radius: 8px;
}

.job-form /deep/ .job-datepicker .dropdown-menu {
  border: 1px solid #b7bec8;
  border-radius: 10px;
  box-shadow: 0 10px 25px rgba(17, 24, 39, 0.12);
}

.job-form textarea.form-control {
  min-height: 92px;
}

@media (max-width: 575px) {
  .job-form {
    padding: 0.85rem;
  }
}
</style>
